import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Base from "../../Base/views/Base";
import Box from '@mui/material/Box';
import {
    Button,
    Card,
    Grid,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
    IconButton,
    Divider,
    Stack
} from "@mui/material";
import Popup from "../../components/Popup";
import Swal from "sweetalert2";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import axiosInstance from "../../../axiosInstance";

const InventoryLandingPage = () => {
    const [hotelID] = useState(localStorage.getItem("billing_app_hotel"));
    const [data, setData] = useState([]);
    const [categories, setCategories] = useState([]);
    const [categoryFilter, setCategoryFilter] = useState("");
    const [open, setOpen] = useState(false);
    const [transactionOpen, setTransactionOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);

    const initialValues = {
        name: "",
        description: "",
        category: "",
        unit: "pcs",
        current_stock: 0,
        minimum_stock: 0,
        unit_price: 0,
        supplier: "",
        hotel: ""
    };

    const transactionInitialValues = {
        inventory_item: "",
        transaction_type: "purchase",
        quantity: "",
        unit_price: "",
        reference: "",
        notes: ""
    };

    const [inputData, setInputData] = useState(initialValues);
    const [transactionData, setTransactionData] = useState(transactionInitialValues);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        getCategories();
    }, []);

    useEffect(() => {
        getData();
    }, [categoryFilter]);

    const getData = () => {
        const url = categoryFilter
            ? `/api/inventory/?hotel=${hotelID}&category=${categoryFilter}`
            : `/api/inventory/?hotel=${hotelID}`;
        axiosInstance(url, { method: "GET" }).then(res => {
            setData(res.data);
        }).catch(err => {
            console.log(err);
        });
    };

    const getCategories = () => {
        axiosInstance(`/api/inventory-category/?hotel=${hotelID}`, {
            method: "GET"
        }).then(res => {
            setCategories(res.data);
        }).catch(err => {
            console.error(err);
        });
    };

    const validate = () => {
        let temp = { ...errors };
        if (!inputData.name) temp.name = "Name is required";
        if (inputData.minimum_stock < 0) temp.minimum_stock = "Minimum stock cannot be negative";
        if (inputData.unit_price < 0) temp.unit_price = "Unit price cannot be negative";
        setErrors(temp);
        return Object.values(temp).every(x => x === "");
    };

    const validateTransaction = () => {
        let temp = {};
        if (!transactionData.inventory_item) temp.inventory_item = "Item is required";
        if (!transactionData.quantity || transactionData.quantity <= 0) temp.quantity = "Valid quantity is required";
        return Object.values(temp).every(x => x === "");
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setInputData({ ...inputData, [name]: value });
        if (errors[name]) {
            setErrors({ ...errors, [name]: "" });
        }
    };

    const handleTransactionChange = (e) => {
        const { name, value } = e.target;
        setTransactionData({ ...transactionData, [name]: value });
    };

    const handleClose = () => {
        setOpen(false);
        setInputData(initialValues);
        setErrors({});
    };

    const handleTransactionClose = () => {
        setTransactionOpen(false);
        setTransactionData(transactionInitialValues);
        setSelectedItem(null);
    };

    const handleSubmit = () => {
        if (validate()) {
            const api = inputData?.id ? `/api/inventory/${inputData.id}/` : `/api/inventory/`;
            const method = inputData?.id ? "PUT" : "POST";

            const payload = {
                name: inputData.name,
                description: inputData.description,
                unit: inputData.unit,
                current_stock: inputData.current_stock,
                minimum_stock: inputData.minimum_stock,
                unit_price: inputData.unit_price,
                supplier: inputData.supplier,
                hotel: hotelID
            };
            if (inputData.category) payload.category = inputData.category;
            axiosInstance(api, {
                method: method,
                data: payload
            }).then(res => {
                getData();
                setOpen(false);
                setInputData(initialValues);
                Swal.fire({
                    icon: 'success',
                    title: inputData?.id ? 'Item updated!' : 'Item added!',
                    timer: 1500
                });
            }).catch(err => {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to save item'
                });
            });
        }
    };

    const handleTransactionSubmit = () => {
        if (validateTransaction()) {
            axiosInstance('/api/stock-transaction/', {
                method: "POST",
                data: transactionData
            }).then(res => {
                getData();
                setTransactionOpen(false);
                setTransactionData(transactionInitialValues);
                setSelectedItem(null);
                Swal.fire({
                    icon: 'success',
                    title: 'Transaction recorded!',
                    timer: 1500
                });
            }).catch(err => {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to record transaction'
                });
            });
        }
    };

    const handleClick = (item) => {
        setInputData(item);
        setOpen(true);
    };

    const handleTransactionClick = (item) => {
        setSelectedItem(item);
        setTransactionData({
            ...transactionInitialValues,
            inventory_item: item.id,
            unit_price: item.unit_price
        });
        setTransactionOpen(true);
    };

    const handleDelete = (e, id) => {
        e?.stopPropagation();
        Swal.fire({
            title: "Are you sure?",
            text: "This item will be deleted.",
            showCancelButton: true,
            confirmButtonText: "Delete",
            icon: 'warning',
        }).then((result) => {
            if (result.isConfirmed) {
                axiosInstance(`/api/inventory/${id}/`, {
                    method: "DELETE"
                }).then(res => {
                    getData();
                    Swal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        timer: 1500
                    });
                }).catch(err => {
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: 'Failed to delete item'
                    });
                });
            }
        });
    };

    const lowStockItems = data.filter(item => item.is_low_stock);
    const totalStockValue = data.reduce((sum, item) => sum + parseFloat(item.stock_value || 0), 0);

    useEffect(() => {
        if (categoryFilter !== undefined) getData();
    }, [categoryFilter]);

    return (
        <Base>
            <Popup
                open={open}
                handleClose={handleClose}
                title={inputData?.id ? "Edit Inventory Item" : "Add Inventory Item"}
                handleSubmit={handleSubmit}
                save={true}
                maxWidth="md"
            >
                <Box sx={{ mt: 2 }}>
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <InputLabel>Category</InputLabel>
                        <Select
                            name="category"
                            value={inputData.category || ""}
                            onChange={handleChange}
                            label="Category"
                        >
                            <MenuItem value="">None</MenuItem>
                            {categories.map((cat) => (
                                <MenuItem key={cat.id} value={cat.id}>
                                    {cat.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        label="Item Name"
                        fullWidth
                        required
                        name="name"
                        value={inputData.name || ""}
                        onChange={handleChange}
                        size="small"
                        sx={{ mb: 2 }}
                        error={!!errors.name}
                        helperText={errors.name}
                    />

                    <TextField
                        label="Description"
                        fullWidth
                        multiline
                        rows={2}
                        name="description"
                        value={inputData.description || ""}
                        onChange={handleChange}
                        size="small"
                        sx={{ mb: 2 }}
                    />

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Unit</InputLabel>
                                <Select
                                    name="unit"
                                    value={inputData.unit || "pcs"}
                                    onChange={handleChange}
                                    label="Unit"
                                >
                                    <MenuItem value="kg">Kilogram</MenuItem>
                                    <MenuItem value="g">Gram</MenuItem>
                                    <MenuItem value="l">Liter</MenuItem>
                                    <MenuItem value="ml">Milliliter</MenuItem>
                                    <MenuItem value="pcs">Pieces</MenuItem>
                                    <MenuItem value="box">Box</MenuItem>
                                    <MenuItem value="pack">Pack</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label="Unit Price"
                                type="number"
                                fullWidth
                                name="unit_price"
                                value={inputData.unit_price || ""}
                                onChange={handleChange}
                                size="small"
                                error={!!errors.unit_price}
                                helperText={errors.unit_price}
                                InputProps={{
                                    startAdornment: <span style={{ marginRight: 8 }}>₹</span>
                                }}
                            />
                        </Grid>
                    </Grid>

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={6}>
                            <TextField
                                label="Current Stock"
                                type="number"
                                fullWidth
                                name="current_stock"
                                value={inputData.current_stock || ""}
                                onChange={handleChange}
                                size="small"
                                disabled={!!inputData.id}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label="Minimum Stock (Alert Level)"
                                type="number"
                                fullWidth
                                name="minimum_stock"
                                value={inputData.minimum_stock || ""}
                                onChange={handleChange}
                                size="small"
                                error={!!errors.minimum_stock}
                                helperText={errors.minimum_stock}
                            />
                        </Grid>
                    </Grid>

                    <TextField
                        label="Supplier"
                        fullWidth
                        name="supplier"
                        value={inputData.supplier || ""}
                        onChange={handleChange}
                        size="small"
                    />
                </Box>
            </Popup>

            <Popup
                open={transactionOpen}
                handleClose={handleTransactionClose}
                title="Stock Transaction"
                handleSubmit={handleTransactionSubmit}
                save={true}
                maxWidth="sm"
            >
                <Box sx={{ mt: 2 }}>
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <InputLabel>Transaction Type</InputLabel>
                        <Select
                            name="transaction_type"
                            value={transactionData.transaction_type || "purchase"}
                            onChange={handleTransactionChange}
                            label="Transaction Type"
                        >
                            <MenuItem value="purchase">Purchase</MenuItem>
                            <MenuItem value="sale">Sale</MenuItem>
                            <MenuItem value="adjustment">Adjustment</MenuItem>
                            <MenuItem value="waste">Waste</MenuItem>
                            <MenuItem value="return">Return</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        label="Quantity"
                        type="number"
                        fullWidth
                        required
                        name="quantity"
                        value={transactionData.quantity || ""}
                        onChange={handleTransactionChange}
                        size="small"
                        sx={{ mb: 2 }}
                    />

                    <TextField
                        label="Unit Price"
                        type="number"
                        fullWidth
                        name="unit_price"
                        value={transactionData.unit_price || ""}
                        onChange={handleTransactionChange}
                        size="small"
                        sx={{ mb: 2 }}
                        InputProps={{
                            startAdornment: <span style={{ marginRight: 8 }}>₹</span>
                        }}
                    />

                    <TextField
                        label="Reference (Order ID, Invoice #, etc.)"
                        fullWidth
                        name="reference"
                        value={transactionData.reference || ""}
                        onChange={handleTransactionChange}
                        size="small"
                        sx={{ mb: 2 }}
                    />

                    <TextField
                        label="Notes"
                        fullWidth
                        multiline
                        rows={2}
                        name="notes"
                        value={transactionData.notes || ""}
                        onChange={handleTransactionChange}
                        size="small"
                    />
                </Box>
            </Popup>

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, gap: 1.5, flexWrap: 'wrap' }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Filter by Category</InputLabel>
                    <Select
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value)}
                        label="Filter by Category"
                    >
                        <MenuItem value="">All</MenuItem>
                        {categories.map((cat) => (
                            <MenuItem key={cat.id} value={cat.id}>
                                {cat.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Box sx={{ display: "flex", gap: 1.5, flexWrap: 'wrap', alignItems: "center" }}>
                <Card 
                    sx={{ 
                        p: 1.5, 
                        minWidth: 140, 
                        textAlign: "center",
                        background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                        border: '1px solid rgba(25, 118, 210, 0.1)',
                    }}
                >
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25, fontWeight: 500, fontSize: '0.75rem' }}>
                        Total Stock Value
                    </Typography>
                    <Typography variant="h6" color="primary" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                        ₹{totalStockValue.toFixed(2)}
                    </Typography>
                </Card>
                {lowStockItems.length > 0 && (
                    <Card 
                        sx={{ 
                            p: 1.5, 
                            bgcolor: "#fff3e0", 
                            minWidth: 160,
                            border: '1px solid rgba(255, 152, 0, 0.2)',
                        }}
                    >
                        <Typography variant="caption" color="warning.main" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>
                            ⚠️ {lowStockItems.length} item(s) low on stock
                        </Typography>
                    </Card>
                )}
                <Button 
                    variant="contained" 
                    onClick={() => setOpen(true)}
                    size="small"
                    sx={{ 
                        borderRadius: 1.5,
                        px: 2,
                        py: 0.75,
                        textTransform: 'none',
                        fontWeight: 600,
                        boxShadow: '0 2px 8px rgba(139, 68, 255, 0.3)',
                        '&:hover': {
                            boxShadow: '0 4px 12px rgba(139, 68, 255, 0.4)',
                            transform: 'translateY(-1px)',
                        },
                        transition: 'all 0.3s ease',
                    }}
                >
                    Add Item
                </Button>
                <Button 
                    component={Link}
                    to="/inventory-category"
                    variant="outlined"
                    color="primary"
                    size="small"
                    sx={{ 
                        borderRadius: 1.5,
                        px: 2,
                        py: 0.75,
                        textTransform: 'none',
                        fontWeight: 600,
                        borderColor: 'primary.main',
                        color: 'primary.main',
                        '&:hover': {
                            borderColor: 'primary.dark',
                            color: 'primary.dark',
                            backgroundColor: 'action.hover',
                        },
                        transition: 'all 0.3s ease',
                    }}
                >
                    Inventory Categories
                </Button>
                </Box>
            </Box>

            <Box sx={{ flexGrow: 1 }}>
                <Grid container spacing={2}>
                    {data.length === 0 ? (
                        <Grid item xs={12}>
                            <Box 
                                sx={{ 
                                    mt: 8, 
                                    textAlign: "center",
                                    py: 8,
                                }}
                            >
                                <Typography 
                                    variant="h5" 
                                    sx={{ 
                                        color: 'text.secondary',
                                        mb: 2,
                                        fontWeight: 500,
                                    }}
                                >
                                    No inventory items yet
                                </Typography>
                                <Typography 
                                    variant="body1" 
                                    sx={{ 
                                        color: 'text.secondary',
                                        mb: 4,
                                    }}
                                >
                                    Start managing your inventory by adding your first item
                                </Typography>
                                <Button 
                                    variant="contained" 
                                    onClick={() => setOpen(true)}
                                    sx={{
                                        borderRadius: 2,
                                        px: 4,
                                        py: 1.5,
                                        textTransform: 'none',
                                        fontWeight: 600,
                                    }}
                                >
                                    Add Your First Item
                                </Button>
                            </Box>
                        </Grid>
                    ) : (
                        data.map((item) => (
                            <Grid item xs={12} sm={6} md={4} key={item.id}>
                                <Card
                                    variant="outlined"
                                    sx={{
                                        width: "100%",
                                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                        borderRadius: 2,
                                        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)',
                                        "&:hover": {
                                            boxShadow: '0 4px 12px rgba(139, 68, 255, 0.15)',
                                            transform: "translateY(-3px)",
                                            borderColor: item.is_low_stock ? "warning.main" : "primary.main",
                                        },
                                        cursor: "pointer",
                                        p: 1.5,
                                        border: item.is_low_stock 
                                            ? "2px solid #ff9800" 
                                            : "1px solid rgba(0, 0, 0, 0.08)",
                                        background: item.is_low_stock 
                                            ? 'linear-gradient(135deg, #fff8e1 0%, #fff3e0 100%)'
                                            : 'transparent',
                                    }}
                                    onClick={() => handleClick(item)}
                                >
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                                        <Box>
                                            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                                                {item.name}
                                            </Typography>
                                            {item.category_name && (
                                                <Chip label={item.category_name} size="small" sx={{ mt: 0.5 }} />
                                            )}
                                            {item.description && (
                                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                    {item.description}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Box>
                                            <IconButton size="small" onClick={(e) => {
                                                e.stopPropagation();
                                                handleTransactionClick(item);
                                            }}>
                                                <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>+</Typography>
                                            </IconButton>
                                            <IconButton size="small" onClick={(e) => {
                                                e.stopPropagation();
                                                handleClick(item);
                                            }}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" onClick={(e) => {
                                                e.stopPropagation();
                                                handleDelete(e, item.id);
                                            }}>
                                                <DeleteIcon fontSize="small" color="error" />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                    <Divider sx={{ my: 1 }} />
                                    <Stack spacing={1}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" color="text.secondary">Stock:</Typography>
                                            <Typography fontWeight="bold">{item.current_stock} {item.unit}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" color="text.secondary">Min Stock:</Typography>
                                            <Typography>{item.minimum_stock} {item.unit}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" color="text.secondary">Unit Price:</Typography>
                                            <Typography>₹{parseFloat(item.unit_price).toFixed(2)}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2" color="text.secondary">Stock Value:</Typography>
                                            <Typography fontWeight="bold" color="primary">
                                                ₹{parseFloat(item.stock_value).toFixed(2)}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="body2" color="text.secondary">Status:</Typography>
                                            {item.is_low_stock ? (
                                                <Chip label="Low Stock" color="warning" size="small" />
                                            ) : (
                                                <Chip label="In Stock" color="success" size="small" />
                                            )}
                                        </Box>
                                        {item.supplier && (
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" color="text.secondary">Supplier:</Typography>
                                                <Typography>{item.supplier}</Typography>
                                            </Box>
                                        )}
                                    </Stack>
                                </Card>
                            </Grid>
                        ))
                    )}
                </Grid>
            </Box>
        </Base>
    );
};

export default InventoryLandingPage;
